import type * as React from "react";
import { Tooltip } from "recharts";

import { cn } from "@/lib/utils";

export type ChartConfig = Record<
  string,
  {
    label?: React.ReactNode;
    color?: string;
  }
>;

type ChartContainerProps = React.ComponentProps<"div"> & {
  config: ChartConfig;
};

function ChartContainer({
  className,
  config,
  style,
  ...props
}: ChartContainerProps) {
  const resolvedStyle = {
    ...style,
    ...Object.fromEntries(
      Object.entries(config)
        .filter(([, entry]) => entry.color)
        .map(([key, entry]) => [`--color-${key}`, entry.color])
    ),
  } as React.CSSProperties;

  return (
    <div
      data-slot="chart-container"
      className={cn("w-full", className)}
      style={resolvedStyle}
      {...props}
    />
  );
}

const ChartTooltip = Tooltip;

type ChartTooltipItem = {
  color?: string;
  dataKey?: string | number;
  name?: string | number;
  value?: number | string;
};

type ChartTooltipContentProps = React.HTMLAttributes<HTMLDivElement> & {
  active?: boolean;
  label?: React.ReactNode;
  payload?: ChartTooltipItem[];
  formatter?: (value: number | string) => React.ReactNode;
  indicator?: "line" | "dot";
  hideLabel?: boolean;
};

function ChartTooltipContent({
  active,
  payload,
  label,
  indicator = "dot",
  hideLabel = false,
  className,
  ...props
}: ChartTooltipContentProps) {
  if (!active || !payload?.length) {
    return null;
  }

  return (
    <div
      className={cn(
        "rounded-md border border-border bg-popover px-3 py-2 text-sm shadow-[var(--surface-shadow-sm)]",
        className
      )}
      {...props}
    >
      {!hideLabel ? (
        <div className="mb-2 font-medium text-foreground">
          {typeof label === "string" ? label : String(label)}
        </div>
      ) : null}
      <div className="space-y-1">
        {(payload as ChartTooltipItem[]).map((item) => {
          const itemLabel =
            typeof item.name === "string" && item.name.length > 0
              ? item.name
              : typeof item.dataKey === "string"
                ? item.dataKey
                : "Series";
          const itemValue =
            typeof item.value === "number"
              ? new Intl.NumberFormat("en-GB").format(item.value)
              : item.value;

          return (
            <div key={String(itemLabel)} className="flex items-center gap-2">
              <span
                aria-hidden="true"
                className={cn(
                  "shrink-0 rounded-full",
                  indicator === "line" ? "h-0.5 w-3" : "size-2"
                )}
                style={{ backgroundColor: item.color }}
              />
              <span className="text-muted-foreground">{itemLabel}</span>
              <span className="ml-auto font-medium text-foreground">
                {itemValue}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export { ChartContainer, ChartTooltip, ChartTooltipContent };
